import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TFieldType } from "store/reducers/promotion.reducers";
import { Box, Chip, DatePicker, Text } from "common";
import { Timing } from "custom";
import { format } from "date-fns-tz";
import { useTimeZoneSupport } from "hooks";

interface IDateRangeWithTimingProps {
  disable: boolean;
  isMilestone?: boolean;
  field: TFieldType;
  onValidateInput: (error: any) => void;
  getValue: (keys: string[]) => any;
  onChange: (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => void;
  fieldError?: boolean;
  gap?: string;
  testId?: string;
  maxYearsAllowed?: number;
  isEdit?: boolean;
}

const DateRangeWithTiming: React.FC<IDateRangeWithTimingProps> = ({
  disable,
  isMilestone = false,
  field,
  onValidateInput,
  onChange,
  fieldError,
  gap,
  testId,
  maxYearsAllowed = 20,
  getValue,
  isEdit
}) => {
  const { timeZone, timeZoneToDate, dateToTimeZone } = useTimeZoneSupport();
  const tomorrow = useMemo(
    () =>
      new Date(
        new Date(new Date().setHours(0, 0, 0, 0)).getTime() +
          24 * 60 * 60 * 1000
      ),
    []
  );
  const [startDate, setStartDate] = useState<Date | null>(
    // comment the tomorrow variable and add current date 
    (dateToTimeZone(getValue(field.startDateKeys || [])) || new Date() /*tomorrow*/) as Date
  );
  const [endDate, setEndDate] = useState<Date | null>(
    (dateToTimeZone(getValue(field.endDateKeys || [])) || tomorrow) as Date
  );

  const endDateFromStore = getValue(field.endDateKeys || []);

  const [showTime, setShowTime] = useState(
    field.withTiming && !field.isTimingOneDay
  );
  const { withTiming, isTimingOneDay, defaultValue } = useMemo(
    () => field,
    [field]
  );
  const [startDateErr, setStartDateErr] = useState("");
  const [endDateErr, setEndDateErr] = useState("");
  const [startTimeErr, setStartTimeErr] = useState("");
  const [endTimeErr, setEndTimeErr] = useState("");

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const maxYearAllowed = useMemo(
    () => maxYearsAllowed + new Date().getFullYear(),
    [maxYearsAllowed]
  );

  // set redux store value or default timing
  const [startDefaultTime, endDefaultTime] = useMemo(() => {
    if (
      startDate &&
      endDate &&
      (startDate !== tomorrow || endDate !== tomorrow)
    ) {
      const [startHours, startMinutes, startSeconds] = [
        startDate.getHours(),
        startDate.getMinutes(),
        startDate.getSeconds(),
      ];
      const [endHours, endMinutes, endSeconds] = [
        endDate.getHours(),
        endDate.getMinutes(),
        endDate.getSeconds(),
      ];
      return [
        `${startHours < 10 ? `0${startHours}` : startHours}:${
          startMinutes < 10 ? `0${startMinutes}` : startMinutes
        }:${startSeconds < 10 ? `0${startSeconds}` : startSeconds}`,
        `${endHours < 10 ? `0${endHours}` : endHours}:${
          endMinutes < 10 ? `0${endMinutes}` : endMinutes
        }:${endSeconds < 10 ? `0${endSeconds}` : endSeconds}`,
      ];
    } else {
      if (defaultValue && typeof defaultValue !== "string") {
        const { endTime, startTime } = defaultValue;
        return [startTime, endTime];
      }
    }
    return [];
  }, [defaultValue, startDate, endDate, tomorrow]);

  const onChangeStartTime = (timing: string) => {
    setStartTime(timing);
  };

  const onChangeEndTime = (timing: string) => {
    setEndTime(timing);
  };

  const onChangeStartDate = (date: Date | null) => {
    setStartDateErr("");
    let today = new Date();
    today.setHours(0, 0, 0, 0,);
    if (date) {
      if(date < today) {
        setStartDateErr("Start date cannot be earlier than today date");
      } else {
        setStartDate(new Date(date.setHours(0, 0, 0, 0)));
      }
    } else {
      setStartDateErr("Please enter correct date");
      setStartDate(null);
    }
  };

  const onChangeEndDate = (date: Date | null) => {
    setEndDateErr("");
    if (date) {
      setEndDate(new Date(date.setHours(0, 0, 0, 0)));
    } else {
      setEndDateErr("Please enter correct date");
      setEndDate(null);
    }
  };

  useEffect(() => {
    if (
      ((isTimingOneDay && startDate === endDate) || !isTimingOneDay) &&
      withTiming
    ) {
      setShowTime(true);
    } else {
      setShowTime(false);
    }
  }, [startDate, endDate, isTimingOneDay, withTiming]);

  const validateDate = useCallback(() => {
    let isValid = true;
    let startDateTime = 0;
    let endDateTime = 0;
    setStartDateErr("");
    setEndDateErr("");
    const today = new Date();
    const todayTime = today.getTime();
    if (!startDate || isNaN(startDate.getTime())) {
      isValid = false;
      setStartDateErr("Please enter correct date");
    }
    if (!endDate || isNaN(endDate.getTime())) {
      isValid = false;
      setEndDateErr("Please enter correct date");
    }
    if (startDate) {
      startDateTime = startDate.getTime();
    }
    if (endDate) {
      endDateTime = endDate.getTime();
    }
    // Start date Errors Validation
    if (startDate && startDate.getFullYear() > maxYearAllowed) {
      setStartDateErr(
        `Start date year has to be earlier than or equal to ${maxYearAllowed}`
      );
      isValid = false;
    }
    // REMOVE VALIDATOR OF TODAY FOR START DATE
    // else if (!disable && isEdit && startDateTime && startDateTime < todayTime) {
    //   setStartDateErr(
    //     "Start date cannot be earlier than or equal to today date"
    //   );
    //   isValid = false;
    // } 
    else if (startDateTime && endDateTime) {
      if (startDateTime > endDateTime) {
        setStartDateErr("Start Date cannot be later than end date");
        isValid = false;
      }
    }
    // end Date Errors validation
    if (endDate && endDate.getFullYear() > maxYearAllowed) {
      setEndDateErr(
        `End date year has to be earlier than or equal to ${maxYearAllowed}`
      );
      isValid = false;
    } else if (endDateTime && endDateTime <= todayTime) {
      setEndDateErr("End date cannot be earlier than or equal to today date");
      isValid = false;
    }
    return isValid;
  }, [startDate, endDate, maxYearAllowed]);

  const { startDate: validatedStartDate, endDate: validatedEndDate } =
    useMemo(() => {
      let newStartDate = startDate;
      let newEndDate = endDate;
      const startDateTime = startDate ? startDate.getTime() : 0;
      const endDateTime = endDate ? endDate.getTime() : 0;
      if (startDate && endDate) {
        if (validateDate()) {
          if (showTime && withTiming) {
            if (startTime && endTime) {
              const [startHours, startMinutes, startSeconds] =
                startTime.split(":");
              const [endHours, endMinutes, endSeconds] = endTime.split(":");
              if (
                startDateTime &&
                endDateTime &&
                startDateTime === endDateTime
              ) {
                if (
                  +startHours < +endHours ||
                  (+startHours === +endHours && +startMinutes < +endMinutes)
                ) {
                  newStartDate = new Date(
                    new Date(startDate).setHours(
                      +startHours,
                      +startMinutes,
                      +startSeconds
                    )
                  );
                  newEndDate = new Date(
                    new Date(endDate).setHours(
                      +endHours,
                      +endMinutes,
                      +endSeconds
                    )
                  );
                  setStartDateErr("");
                  setEndDateErr("");
                  setStartTimeErr("");
                  setEndTimeErr("");
                } else {
                  newStartDate = null;
                  newEndDate = null;
                  setStartTimeErr(
                    "Start time cannot be later than or equal end time"
                  );
                  setEndTimeErr(
                    "End time cannot be earlier than or equal start time"
                  );
                }
              } else {
                newStartDate = new Date(
                  new Date(startDate).setHours(
                    +startHours,
                    +startMinutes,
                    +startSeconds
                  )
                );
                newEndDate = new Date(
                  new Date(endDate).setHours(
                    +endHours,
                    +endMinutes,
                    +endSeconds
                  )
                );
                setStartDateErr("");
                setEndDateErr("");
                setStartTimeErr("");
                setEndTimeErr("");
              }
            } else {
              if (!startTime) {
                setStartTimeErr("Please enter the start time");
              } else if (!endTime) {
                setEndTimeErr("Please enter the end time");
              }
              newStartDate = null;
              newEndDate = null;
            }
          }
        } else {
          newStartDate = null;
          newEndDate = null;
        }
      }
      return { startDate: newStartDate, endDate: newEndDate };
    }, [
      validateDate,
      startDate,
      endDate,
      startTime,
      endTime,
      showTime,
      withTiming,
    ]);

  useEffect(() => {
    const convertedStartDateToTimeZone = timeZoneToDate(validatedStartDate, {
      isIsoString: true,
    }) as Date | null;
    const convertedEndDateToTimeZone = timeZoneToDate(validatedEndDate, {
      isIsoString: true,
    }) as Date | null;
    onChange({
      startDate: convertedStartDateToTimeZone,
      endDate: convertedEndDateToTimeZone,
    });
    // eslint-disable-next-line
  }, [validatedEndDate, validatedStartDate]);

  useEffect(() => {
    if (!startDateErr && !endDateErr && !startTimeErr && !endTimeErr) {
      if (fieldError) {
        onValidateInput({ [field.name]: { isError: false } });
      }
    } else {
      if (!fieldError) {
        onValidateInput({ [field.name]: { isError: true } });
      }
    }
    // eslint-disable-next-line
  }, [startDateErr, endDateErr, startTimeErr, endTimeErr]);

  // This is used to sync end date and time for milestone reward
  useEffect(() => {
    if (!isMilestone) return;
    const updatedEndDate = dateToTimeZone(endDateFromStore) as Date | null;

    if (!updatedEndDate) return;
    // Sync END date
    if (updatedEndDate.getTime() !== endDate?.getTime()) {
      setEndDate(updatedEndDate);
    }

    // Extract END TIME from same source
    const hours = updatedEndDate.getHours();
    const minutes = updatedEndDate.getMinutes();
    const seconds = updatedEndDate.getSeconds();
    const formattedTime = `${hours < 10 ? `0${hours}` : hours}:${
      minutes < 10 ? `0${minutes}` : minutes
    }:${seconds < 10 ? `0${seconds}` : seconds}`;

    // Sync END TIME
    if (formattedTime !== endTime) {
      setEndTime(formattedTime);
    }
  }, [endDateFromStore, isMilestone]);

  return (
    <Box
      gridTemplateColumns={["1fr", "1fr", "1fr 1fr", "1fr 1fr"]}
      gridGap={gap}
      data-testid={testId}
    >
      <Box gridTemplateColumns="1fr" gridGap="15px">
        <DatePicker
          disabled={disable}
          id="start-date-picker"
          label="Start date"
          value={startDate}
          onChange={onChangeStartDate}
          error={!!startDateErr}
          errorMessage={startDateErr}
          variant="inline"
          inputVariant="filled"
          disableUnderLine={false}
          width="100%"
          size="medium"
          disablePast={true}
          testId="date-picker-start"
        />
        {showTime && withTiming && (
          <Timing
            disable={disable}
            timingVariant="start"
            onChange={onChangeStartTime}
            errorMsg={startTimeErr}
            defaultValue={startDefaultTime}
            testId="timing"
            id="start-timing"
          />
        )}
        {validatedStartDate && !isNaN(validatedStartDate.getTime()) && (
          <Chip
            color="primary"
            borderRadius="10px"
            padding="3px"
            testId="chip-start-date-time"
            label={
              <Text fontSize="13px" fontWeight={600} color="common.white">
                {format(validatedStartDate, "MMMM d','yyyy 'at' HH:mm:ss XX", {
                  timeZone,
                })}
              </Text>
            }
          />
        )}
      </Box>
      <Box gridTemplateColumns="1fr" gridGap="15px">
        <DatePicker
          disabled={isMilestone}
          label="End date"
          id="end-date-picker"
          value={endDate}
          onChange={onChangeEndDate}
          error={!!endDateErr}
          errorMessage={endDateErr}
          variant="inline"
          inputVariant="filled"
          disableUnderLine={false}
          width="100%"
          size="medium"
          disablePast={true}
          testId="date-picker-end"
        />
        {showTime && withTiming && (
          <Timing
            disable={isMilestone}
            timingVariant="end"
            onChange={onChangeEndTime}
            value={isMilestone ? endTime : ""} 
            errorMsg={endTimeErr}
            defaultValue={endDefaultTime}
            testId="timing"
            id="end-timing"
          />
        )}
        {validatedEndDate && !isNaN(validatedEndDate.getTime()) && (
          <Chip
            color="primary"
            borderRadius="10px"
            padding="3px"
            testId="chip-end-date-time"
            label={
              <Text fontSize="13px" fontWeight={600} color="common.white">
                {format(validatedEndDate, "MMMM d','yyyy 'at' HH:mm:ss XX", {
                  timeZone,
                })}
              </Text>
            }
          />
        )}
      </Box>
    </Box>
  );
};

export default DateRangeWithTiming;
